import type { Plan } from '@trippath/shared';

import { offsetMinutes, plusDays } from '../../lib/countryFacts';

/** A local hour's part of the day: night 00–04, early morning 04–07, morning 07–11, midday
 * 11–12, lunch 12–14, afternoon 14–17, evening 17–21, late evening 21–24. */
export type Part =
  'night' | 'dawn' | 'morning' | 'midday' | 'lunch' | 'afternoon' | 'evening' | 'late';

/** The parts of the day in order, each from its first hour. */
export const PARTS: readonly (readonly [Part, number])[] = [
  ['night', 0],
  ['dawn', 4],
  ['morning', 7],
  ['midday', 11],
  ['lunch', 12],
  ['afternoon', 14],
  ['evening', 17],
  ['late', 21],
];

/** What a part of the day is to work in: the working day, lunch, early or evening, late, night. */
export type Shade = 'day' | 'lunch' | 'evening' | 'late' | 'night';

export const SHADES: Record<Part, Shade> = {
  night: 'night',
  dawn: 'evening',
  morning: 'day',
  midday: 'day',
  lunch: 'lunch',
  afternoon: 'day',
  evening: 'evening',
  late: 'late',
};

/** Where working hours fall: by day, early or in the evening, late in the evening, at night. */
export type Verdict = 'day' | 'evening' | 'late' | 'night';

/** A minute of the day, whichever day it falls on: −60 is 23:00. */
export function wrap(minute: number): number {
  return ((minute % 1440) + 1440) % 1440;
}

/** A minute of the local day's part of the day. */
export function partOf(minute: number): Part {
  const hour = Math.floor(wrap(minute) / 60);
  let part: Part = 'night';
  for (const [name, from] of PARTS) if (from <= hour) part = name;
  return part;
}

/** A minute of the day as a clock says it: `09:30`; the day's end `24:00`. */
export function clockOf(minute: number): string {
  const shown = minute === 1440 ? 1440 : wrap(minute);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(shown / 60))}:${pad(shown % 60)}`;
}

/** When home's working hours fall where the clock is `shift` minutes ahead: the worst of them —
 * at night, else early or late, else by day. */
export function verdictOf(from: number, to: number, shift: number): Verdict {
  const shades: Shade[] = [];
  for (let minute = from; minute < to; minute += 30)
    shades.push(SHADES[partOf(minute + shift)]);
  if (shades.includes('night')) return 'night';
  if (shades.includes('late')) return 'late';
  return shades.includes('evening') ? 'evening' : 'day';
}

/** A stretch of the trip on one clock: one country's towns one after another at one offset. */
export interface Place {
  country: string;
  towns: string[];
  zone: string;
  first: string;
  last: string;
  /** Its offset from UTC at its first day, in minutes. */
  offset: number;
}

/** The trip's places on their clocks, in order — a change on the way too, Abu Dhabi for its hours
 * there; a stop not yet placed is not one. */
export function placesOf(plan: Plan): Place[] {
  const places: Place[] = [];
  for (const stop of plan.stops) {
    if (stop.zone === null) continue;
    const offset = offsetMinutes(stop.zone, stop.start);
    const last = places.at(-1);
    const end = plusDays(stop.start, stop.days);
    if (last && last.country === stop.country && last.offset === offset) {
      if (!last.towns.includes(stop.city)) last.towns.push(stop.city);
      last.last = end;
    } else {
      places.push({
        country: stop.country,
        towns: [stop.city],
        zone: stop.zone,
        first: stop.start,
        last: end,
        offset,
      });
    }
  }
  return places;
}

/** A time typed, `9`, `09:30` or `0930`, in minutes after midnight up to `24:00`; `null` else. */
export function minutesOf(typed: string): number | null {
  const found = /^(\d{1,2})(?::?(\d{2}))?$/.exec(typed.trim());
  if (found === null) return null;
  const [, hours = '', minutes = '0'] = found;
  const total = Number(hours) * 60 + Number(minutes);
  return Number(minutes) < 60 && total <= 1440 ? total : null;
}
